import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { BillingRecord, IndustryPlan, IndustrySubscription } from '@/types';
import { subscriptionService } from '@/services/industry/subscriptionService';
import {
  recurringSubscriptionService, normalizePlan,
  type ActiveRecurringSubscription, type SubscriptionInvoice,
} from '@/services/checkout/recurringSubscriptionService';
import { freeTrialPlan } from '@/services/industry/planService';
import { FREE_TRIAL_TOKENS } from '@/components/landing/pricingData';
import { useAuth } from '@/contexts/AuthContext';
import { reportReferralConversion } from '@/lib/referral';

interface IndustrySubscriptionContextType {
  active: IndustrySubscription | null;
  history: IndustrySubscription[];
  billing: BillingRecord[];
  /** Real Stripe recurring subscription (with `stripeSubscriptionId`), when one exists. */
  recurring: ActiveRecurringSubscription | null;
  /** Real Stripe invoice history, newest first. */
  recurringInvoices: SubscriptionInvoice[];
  loading: boolean;
  hasActiveSubscription: boolean;
  /** A PAID plan is active (trial does NOT count). Gate /company on this. */
  hasPaidSubscription: boolean;
  isOnFreeTrial: boolean;
  /** Remaining free-trial tokens (token-based trial; 0 when not on a trial). */
  trialTokens: number;
  /**
   * Spend tokens for a metered AI action. Returns true if affordable (and debits
   * the trial balance); false if the trial is exhausted (and raises tokenError to
   * prompt an upgrade). Paid plans are not metered in this demo.
   */
  spendTokens: (amount: number) => Promise<boolean>;
  /** Set to the attempted cost when a spend fails; drives the upgrade prompt. */
  tokenError: number | null;
  dismissTokenError: () => void;
  activate: (input: {
    industryId: string;
    plan: IndustryPlan;
    billingInterval: 'monthly' | 'yearly';
    isFreeTrial?: boolean;
  }) => Promise<IndustrySubscription>;
  cancel: (reason?: string) => Promise<void>;
  upgrade: (planId: string) => Promise<void>;
  downgrade: (planId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<IndustrySubscriptionContextType | undefined>(undefined);

export function IndustrySubscriptionProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<IndustrySubscription | null>(null);
  const [history, setHistory] = useState<IndustrySubscription[]>([]);
  const [billing, setBilling] = useState<BillingRecord[]>([]);
  const [recurring, setRecurring] = useState<ActiveRecurringSubscription | null>(null);
  const [recurringInvoices, setRecurringInvoices] = useState<SubscriptionInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const autoGrantedFor = useRef<string | null>(null);
  const [tokenError, setTokenError] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h, b, rec, inv] = await Promise.all([
        subscriptionService.getActive(),
        subscriptionService.getHistory(),
        subscriptionService.getBilling(),
        recurringSubscriptionService.getActive(),
        recurringSubscriptionService.getInvoices(),
      ]);
      setActive(a);
      setHistory(h);
      setBilling(b);
      setRecurring(rec);
      setRecurringInvoices(inv);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for Firebase Auth to finish restoring the session before firing any
  // of these — this provider mounts on app load regardless of auth state, so
  // firing unconditionally raced onAuthStateChanged: the callable requests
  // went out with no ID token attached, the backend correctly 401'd
  // ("Sign-in required"), and every call here silently fell back to its
  // mock — including "no subscription", which then bounced a genuinely
  // signed-in, genuinely subscribed user out of /company/* on first load.
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    void refresh();
  }, [authLoading, user, refresh]);

  const activate: IndustrySubscriptionContextType['activate'] = useCallback(async ({ industryId, plan, billingInterval, isFreeTrial }) => {
    const price = billingInterval === 'yearly' ? plan.priceYearly : plan.priceMonthly;
    const sub = await subscriptionService.create({
      userId: 'user_demo',
      industryId,
      planId: plan.id,
      status: isFreeTrial ? 'trialing' : 'active',
      billingInterval,
      amount: isFreeTrial ? 0 : price,
      currency: plan.currency,
      // Token-based trial: a one-time grant of tokens, no expiry date.
      trialTokens: isFreeTrial ? FREE_TRIAL_TOKENS : undefined,
    });
    await refresh();
    reportReferralConversion('subscription', isFreeTrial ? 0 : price).catch(() => undefined);
    return sub;
  }, [refresh]);

  const stripeSubId = recurring?.stripeSubscriptionId ?? null;

  const cancel = useCallback(async (reason?: string) => {
    // Real Stripe subscription → cancel at period end; else the mock path.
    if (stripeSubId) await recurringSubscriptionService.cancel(stripeSubId, false);
    else await subscriptionService.cancel(reason);
    await refresh();
  }, [refresh, stripeSubId]);

  const changePlan = useCallback(async (planId: string) => {
    const target = normalizePlan(planId);
    if (stripeSubId && target) await recurringSubscriptionService.update(stripeSubId, target);
    else await subscriptionService.upgrade(planId);
    await refresh();
  }, [refresh, stripeSubId]);

  const upgrade = changePlan;
  const downgrade = useCallback(async (planId: string) => {
    const target = normalizePlan(planId);
    if (stripeSubId && target) await recurringSubscriptionService.update(stripeSubId, target);
    else await subscriptionService.downgrade(planId);
    await refresh();
  }, [refresh, stripeSubId]);

  // Auto-provision the 50-token free trial: every signed-in user who has no
  // subscription yet is granted the token trial on first sign-in (once per user).
  useEffect(() => {
    if (!user || loading || active) return;
    if (autoGrantedFor.current === user.uid) return;
    autoGrantedFor.current = user.uid;
    void activate({ industryId: '', plan: freeTrialPlan, billingInterval: 'monthly', isFreeTrial: true });
  }, [user, loading, active, activate]);

  // `active` is the legacy/demo subscription tracker (getBusinessSubscription,
  // company-scoped). `recurring` is the real Stripe-backed subscription
  // (getActiveBusinessSubscription, uid-scoped) — the one createBusinessSubscription
  // actually writes to on checkout. RequireIndustry gates the entire /company/*
  // workspace on hasPaidSubscription, so a real paying user whose company only
  // has a `recurring` record (not an `active` one) must still count as paid,
  // or "Open full workspace" bounces every paid company back to
  // /industry/selection regardless of account.
  const hasActiveSubscription = (!!active && (active.status === 'active' || active.status === 'trialing')) || !!recurring;
  const hasPaidSubscription = active?.status === 'active' || recurring?.status === 'active';
  const isOnFreeTrial = active?.status === 'trialing';
  const trialTokens = isOnFreeTrial ? active?.trialTokens ?? FREE_TRIAL_TOKENS : 0;

  const spendTokens = useCallback(async (amount: number): Promise<boolean> => {
    // Paid plans are not metered in this demo.
    if (active?.status === 'active') return true;
    // Only an active trial can spend; otherwise force an upgrade.
    if (active?.status !== 'trialing' || (active.trialTokens ?? 0) < amount) {
      setTokenError(amount);
      return false;
    }
    const updated = await subscriptionService.consumeTokens(amount);
    setActive(updated);
    return true;
  }, [active]);

  const dismissTokenError = useCallback(() => setTokenError(null), []);

  return (
    <Ctx.Provider value={{ active, history, billing, recurring, recurringInvoices, loading, hasActiveSubscription, hasPaidSubscription, isOnFreeTrial, trialTokens, spendTokens, tokenError, dismissTokenError, activate, cancel, upgrade, downgrade, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useIndustrySubscription() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useIndustrySubscription must be used within an IndustrySubscriptionProvider');
  return ctx;
}
